/**
 * TodoDBService.ts
 *
 * TodoDB와 기존 시스템 간의 브리지 역할
 * 동기화, 에디터, 필터링 등의 통합 서비스 제공
 */

import { TodoItem, TODO_STATUS } from '../TodoItem'
import * as TodoService from '../TodoService'
import { getTodoDB, initTodoDB, isTodoDBEnabled, isTodoDBReady } from './index'

/**
 * TodoDB 시스템 초기화
 */
export const initializeTodoDBSystem = async (): Promise<void> => {
  try {
    // TODO: 설정에서 DB 사용 여부 확인 필요가 없어졌다.
    //console.log('Initializing TodoDB system...')

    // 설정에서 DB 사용 여부 확인 (기본값: true)
    // const todoSettings = settings.getValue('todo') || {}
    // const useIndexedDB = todoSettings.useIndexedDB !== false // 기본값을 true로 설정

    // if (!useIndexedDB) {
    //   console.log('TodoDB is disabled in settings')
    //   return
    // }

    console.log('TodoDB proceeding with initialization...')

    // TodoDB 초기화
    const success = await initTodoDB()

    if (success) {
      console.log('TodoDB system initialized successfully')
    } else {
      console.error('Failed to initialize TodoDB system')
    }
  } catch (error) {
    console.error('Error initializing TodoDB system:', error)
  }
}

/**
 * 동기화 시 DB 업데이트
 *   - folder 의 기존 todos 삭제
 *   - 서버에서 받은 새 todos 저장
 *   - DB가 서버 데이터의 정확한 복사본이 됨
 */
export const syncTodosToDB = async (folderUrl: string, todos: TodoItem[]): Promise<void> => {
  if (!isTodoDBEnabled() || !isTodoDBReady()) {
    console.warn('[syncTodosToDB] TodoDB not available')
    return
  }

  try {
    const todoDB = getTodoDB()
    console.log(`[syncTodosToDB] Starting sync for folder ${folderUrl} with ${todos.length} todos`)

    // 폴더의 기존 todos 삭제
    const existingTodos = await todoDB.getTodosByFolder(folderUrl)
    for (const todo of existingTodos) {
      await todoDB.deleteTodo(todo.url)
    }

    // 새로운 todos 저장
    if (todos.length > 0) {
      await todoDB.saveTodos(todos)
    }

    // folder 별 교체 (folder 의 기존 todos 삭제 후 새 todos 저장)
    // await todoDB.replaceAllTodosInFolder(folderUrl, todos)

    console.log(
      `[syncTodosToDB] Successfully synced ${todos.length} todos to DB for folder: ${folderUrl}`,
    )
  } catch (error) {
    console.error(`[syncTodosToDB] Error syncing todos to DB for folder ${folderUrl}:`, error)
    throw error
  }
}

/**
 * 에디터에서 사용할 통합 Todo 서비스
 */
export class TodoDBEditorService {
  /**
   * Todo 생성
   */
  static async createTodo(todo: TodoItem): Promise<void> {
    await TodoService.saveTodo(todo)
  }

  /**
   * Todo 업데이트
   */
  static async updateTodo(todo: TodoItem): Promise<void> {
    await TodoService.saveTodo(todo)
  }

  /**
   * Todo 삭제
   */
  static async deleteTodo(todoUrl: string, folderUrl?: string): Promise<void> {
    await TodoService.deleteTodo(todoUrl, folderUrl)
  }

  /**
   * Todo 조회 (URL 기준)
   */
  static async getTodoByUrl(todoUrl: string): Promise<TodoItem | null> {
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()
        return await todoDB.getTodo(todoUrl)
      } catch (error) {
        console.error('Failed to get todo from DB:', error)
      }
    }

    // 메모리 캐시에서 검색
    const allTodos = await TodoService.getSyncTodoItems()
    return allTodos.find((todo) => todo.url === todoUrl) || null
  }
}

/**
 * 필터링 서비스 (DB 우선, 메모리 캐시 백업)
 */
export class TodoDBFilterService {
  /**
   * 필터링된 Todo 조회
   */
  static async getFilteredTodos(filterType: string, focusDate: Date | null): Promise<TodoItem[]> {
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        return await TodoService.getFilteredTodosFromDB(filterType, focusDate)
      } catch (error) {
        console.error('Failed to get filtered todos from DB:', error)
      }
    }

    // 기존 TodoFilter 사용
    const { getFilteredTodoItems } = await import('../TodoFilter')
    return getFilteredTodoItems(filterType as any, focusDate)
  }

  /**
   * 필터 카운트 조회
   */
  static async getFilterCounts(): Promise<Record<string, number>> {
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        return await TodoService.getFilterCountsFromDB()
      } catch (error) {
        console.error('Failed to get filter counts from DB:', error)
      }
    }

    // 기존 TodoFilter 사용
    const { getFilterCounts } = await import('../TodoFilter')
    return getFilterCounts() as any
  }

  /**
   * 오늘의 Todo (활성 + 완료)
   */
  static async getTodayTodos(): Promise<{ active: TodoItem[]; completed: TodoItem[] }> {
    if (isTodoDBEnabled() && isTodoDBReady()) {
      try {
        const todoDB = getTodoDB()
        return await todoDB.getTodayTodosWithCompleted()
      } catch (error) {
        console.error('Failed to get today todos from DB:', error)
      }
    }

    // 기존 TodoFilter 사용
    const { getTodayTodosWithCompleted } = await import('../TodoFilter')
    return getTodayTodosWithCompleted()
  }
}

/**
 * 통계 및 분석 서비스
 */
export class TodoDBAnalyticsService {
  /**
   * Todo 통계 조회
   */
  static async getTodoStats(): Promise<{
    total: number
    completed: number
    overdue: number
    today: number
    important: number
  }> {
    const counts = await TodoDBFilterService.getFilterCounts()

    return {
      total: counts.all + counts.completed,
      completed: counts.completed,
      overdue: counts.overdue,
      today: counts.today,
      important: counts.important,
    }
  }

  /**
   * 폴더별 통계
   */
  static async getFolderStats(): Promise<
    Array<{
      folderUrl: string
      folderName: string
      totalTodos: number
      completedTodos: number
      activeTodos: number
    }>
  > {
    if (!isTodoDBEnabled() || !isTodoDBReady()) {
      return []
    }

    try {
      const todoDB = getTodoDB()
      const folders = await todoDB.getSyncFolders()
      const stats = []

      for (const folder of folders) {
        const todos = await todoDB.getTodosByFolder(folder.url)
        const completedTodos = todos.filter((t) => t.status === TODO_STATUS.COMPLETED).length

        stats.push({
          folderUrl: folder.url,
          folderName: folder.displayName || 'Unknown',
          totalTodos: todos.length,
          completedTodos,
          activeTodos: todos.length - completedTodos,
        })
      }

      return stats
    } catch (error) {
      console.error('Failed to get folder stats:', error)
      return []
    }
  }
}

/**
 * DB 관리 서비스
 */
export class TodoDBManagementService {
  /**
   * DB 상태 조회
   */
  static getDBStatus(): {
    enabled: boolean
    ready: boolean
    initialized: boolean
  } {
    return {
      enabled: isTodoDBEnabled(),
      ready: isTodoDBReady(),
      initialized: isTodoDBReady(),
    }
  }

  /**
   * DB 재초기화
   */
  static async reinitializeDB(): Promise<boolean> {
    try {
      return await initTodoDB()
    } catch (error) {
      console.error('Failed to reinitialize DB:', error)
      return false
    }
  }

  /**
   * 모든 Todo 데이터 삭제
   */
  static async clearAllTodos(): Promise<boolean> {
    if (!isTodoDBEnabled() || !isTodoDBReady()) {
      console.warn('TodoDB is not available for clearing todos')
      return false
    }

    try {
      const todoDB = getTodoDB()
      await todoDB.clearAllTodos()
      console.log('All todos cleared successfully')
      return true
    } catch (error) {
      console.error('Failed to clear todos:', error)
      return false
    }
  }

  /**
   * 모든 폴더 데이터 삭제
   */
  static async clearAllFolders(): Promise<boolean> {
    if (!isTodoDBEnabled() || !isTodoDBReady()) {
      console.warn('TodoDB is not available for clearing folders')
      return false
    }

    try {
      const todoDB = getTodoDB()
      await todoDB.clearAllFolders()
      console.log('All folders cleared successfully')
      return true
    } catch (error) {
      console.error('Failed to clear folders:', error)
      return false
    }
  }

  /**
   * 전체 DB 데이터 삭제 (모든 스토어 초기화)
   */
  static async clearAllData(): Promise<boolean> {
    if (!isTodoDBEnabled() || !isTodoDBReady()) {
      console.warn('TodoDB is not available for clearing all data')
      return false
    }

    try {
      const todoDB = getTodoDB()
      await todoDB.clearAllData()
      console.log('All DB data cleared successfully')
      return true
    } catch (error) {
      console.error('Failed to clear all data:', error)
      return false
    }
  }
}
